/**
 * What the library calls things, and what it says when there is nothing to
 * show. One place, so a view has one name on its tab, in the attention list,
 * in the page's heading and in its empty state.
 *
 * "Review overdue", never bare "Overdue": the sidebar's Overdue page is about
 * staff who have not acknowledged a policy, which is a different thing, and
 * the product must not use one word for both.
 */
import type { LibraryRow } from './libraryPage';
import type { LibrarySort, LibraryState, LibraryView } from './libraryQuery';
import { hasFilters } from './libraryQuery';

export const VIEW_LABEL: Record<LibraryView, string> = {
  all: 'All',
  published: 'Published',
  inReview: 'In review',
  drafts: 'Drafts',
  retired: 'Retired',
  reviewOverdue: 'Review overdue',
  reviewDue: 'Due in 30 days',
  ownerUnassigned: 'Owner not assigned',
  ownerInactive: 'Owner inactive',
};

/** One line under the heading for a view that is a condition, saying what the condition is. */
export const VIEW_MEANING: Partial<Record<LibraryView, string>> = {
  reviewOverdue: 'Published policies whose review date has passed, the longest overdue first.',
  reviewDue: 'Published policies due for review in the next 30 days, the soonest first.',
  ownerUnassigned: 'Policies with no owner. Open one to give it an owner. If an import named an owner who has not joined yet, the policy becomes theirs when they join.',
  ownerInactive: 'Policies whose owner has left this organization. Open one and give it a new owner.',
};

/** The views that are tabs of the library, in tab order. The rest are conditions reached from Needs attention. */
export const TAB_VIEWS: LibraryView[] = ['all', 'published', 'inReview', 'drafts', 'retired'];
export const ATTENTION_VIEWS: LibraryView[] = ['reviewOverdue', 'reviewDue', 'ownerUnassigned', 'ownerInactive', 'inReview', 'drafts'];

export const SORT_OPTIONS: { value: `${LibrarySort}:${'asc' | 'desc'}`; label: string }[] = [
  { value: 'title:asc', label: 'Title, A to Z' },
  { value: 'title:desc', label: 'Title, Z to A' },
  { value: 'number:asc', label: 'Policy number' },
  { value: 'number:desc', label: 'Policy number, descending' },
  { value: 'review:asc', label: 'Next review, soonest first' },
  { value: 'updated:desc', label: 'Last updated, newest first' },
  { value: 'updated:asc', label: 'Last updated, oldest first' },
];

const NOTHING: Record<LibraryView, string> = {
  all: 'There are no policies yet.',
  published: 'No policies have been published yet.',
  inReview: 'No policies are waiting for review.',
  drafts: 'No draft policies.',
  retired: 'No policies have been retired.',
  reviewOverdue: 'No published policy is past its review date.',
  reviewDue: 'No published policy is due for review in the next 30 days.',
  ownerUnassigned: 'Every policy has an owner in this organization.',
  ownerInactive: 'No policies currently have an inactive owner.',
};

/**
 * `clearFilters` has no button of its own: "Clear filters" is already in the filter bar above, and two of them is one too many.
 * `searchEverything` keeps what was typed and drops the tab and filters it was confined to.
 */
export interface EmptyState { title: string; hint: string | null; action: 'clearFilters' | 'clearSearch' | 'searchEverything' | 'upload' | null }

/**
 * Why there are no rows, in the reader's terms, with the way out where there
 * is one. Never said for a query that failed: that is an error, and the page
 * shows it as one.
 */
export function emptyState(state: LibraryState, opts: {
  searching: boolean; departmentName?: string | null; categoryName?: string | null; mayUpload: boolean;
  /** The view is narrower than this person's whole library (a tab other than their default). */
  narrowed?: boolean;
}): EmptyState {
  if (opts.searching) {
    // Where the search was confined to, in the reader's own terms: what they
    // chose, not "tabs and filters", which a reader browsing a department has never seen.
    const within = opts.departmentName && state.department ? opts.departmentName
      : opts.categoryName && state.category ? opts.categoryName
        : hasFilters(state) ? 'the filters above'
          : opts.narrowed ? `${VIEW_LABEL[state.view]}` : null;
    return {
      title: `No policies match "${state.text.trim()}".`,
      hint: within ? `This search only looked in ${within}.` : 'Check the spelling, or try fewer or different words.',
      action: within ? 'searchEverything' : 'clearSearch',
    };
  }
  if (state.mine && !state.department && !state.category && !state.documentType) {
    return {
      title: opts.narrowed ? `You do not own any policies in ${VIEW_LABEL[state.view]}.` : 'You do not own any policies.',
      hint: 'My policies shows the policies where you are the owner.', action: 'clearFilters',
    };
  }
  if (hasFilters(state)) {
    // The commonest reader case, said the way a reader would: by what they chose.
    if (state.view === 'published' && state.department && !state.category && !state.documentType && opts.departmentName) {
      return { title: `No published policies are assigned to ${opts.departmentName}.`, hint: null, action: 'clearFilters' };
    }
    if (state.view === 'published' && state.category && !state.department && !state.documentType && opts.categoryName) {
      return { title: `No published policies are in ${opts.categoryName}.`, hint: null, action: 'clearFilters' };
    }
    return { title: 'No policies match these filters.', hint: 'Change a filter, or clear them all.', action: 'clearFilters' };
  }
  return {
    title: NOTHING[state.view],
    hint: (state.view === 'all' || state.view === 'drafts') && opts.mayUpload ? 'Upload one, singly or in batches.' : null,
    action: (state.view === 'all' || state.view === 'drafts') && opts.mayUpload ? 'upload' : null,
  };
}

const DAY_MS = 86_400_000;

/**
 * What the Status column says. Words first, always: a color is never the
 * only thing telling a published policy from one whose review is overdue.
 */
export function rowStatus(row: Pick<LibraryRow, 'status' | 'reviewDate' | 'revisionStatus'>, now: Date): { label: string; tone: 'ok' | 'info' | 'neutral' | 'warn' | 'danger'; detail: string | null } {
  const revising = row.revisionStatus === 'draft' ? 'New version in draft'
    : row.revisionStatus === 'in_review' ? 'New version in review'
      : row.revisionStatus === 'approved' ? 'New version approved' : null;
  if (row.status === 'published') {
    if (row.reviewDate) {
      const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
      const [y, m, d] = row.reviewDate.split('-').map(Number);
      // A calendar day against a calendar day, both as UTC midnights: no time zone can move either.
      const days = Math.round((Date.UTC(y!, m! - 1, d!) - today) / DAY_MS);
      if (days < 0) return { label: 'Review overdue', tone: 'danger', detail: revising };
      if (days <= 30) return { label: 'Review due soon', tone: 'warn', detail: revising };
    }
    return { label: 'Published', tone: 'ok', detail: revising };
  }
  switch (row.status) {
    case 'draft': return { label: 'Draft', tone: 'neutral', detail: null };
    case 'in_review': case 'pending_review': return { label: 'In review', tone: 'info', detail: null };
    case 'approved': return { label: 'Approved', tone: 'info', detail: null };
    case 'retired': return { label: 'Retired', tone: 'neutral', detail: null };
    default: return { label: row.status.charAt(0).toUpperCase() + row.status.slice(1).replace(/_/g, ' '), tone: 'neutral', detail: null };
  }
}

/** What the Owner cell says, for people who are shown owners. */
export function ownerText(owner: LibraryRow['owner']): { text: string; note: string | null; tone: 'plain' | 'warn' } {
  if (!owner) return { text: '—', note: null, tone: 'plain' };
  const waiting = owner.waitingFor ? `Waiting for ${owner.waitingFor} to join` : null;
  if (owner.state === 'unassigned') return { text: 'Not assigned', note: waiting, tone: 'warn' };
  if (owner.state === 'inactive') return { text: owner.name ?? 'Former member', note: waiting ?? 'Has left', tone: 'warn' };
  return { text: owner.name ?? 'Unnamed member', note: waiting, tone: 'plain' };
}
